import { useCallback } from 'react';
import type { DataGridColumn, GroupedRowData } from '../DataGrid.types';
import type { ExcelColumn, GroupedExportData } from '../../utils/excelExport';
import { exportToExcel, exportGroupedToExcel } from '../../utils/excelExport';

export interface UseExportOptions<T extends Record<string, unknown>> {
  data: T[];
  columns: DataGridColumn<T>[];
  groupedData?: GroupedRowData<T>[];
  filename?: string;
  onExport?: (data: T[]) => void;
}

export interface UseExportReturn {
  exportToExcel: () => Promise<void>;
  exportGroupedToExcel: () => Promise<void>;
}

export function useExport<T extends Record<string, unknown>>({
  data,
  columns,
  groupedData,
  filename = 'export.xlsx',
  onExport
}: UseExportOptions<T>): UseExportReturn {
  const excelColumns: ExcelColumn[] = columns.map(col => ({
    header: typeof col.header === 'string' ? col.header : col.id,
    key: col.accessorKey || col.id,
    width: col.width ? col.width / 7 : 15
  }));

  const handleExportToExcel = useCallback(async () => {
    await exportToExcel(data, excelColumns, { filename });
    onExport?.(data);
  }, [data, excelColumns, filename, onExport]);

  const handleExportGroupedToExcel = useCallback(async () => {
    if (!groupedData || groupedData.length === 0) {
      await handleExportToExcel();
      return;
    }

    const flatData: GroupedExportData[] = [];

    function flatten(groups: GroupedRowData<T>[], level: number = 0) {
      groups.forEach(group => {
        flatData.push({
          isGroup: true,
          level,
          groupValue: String(group.groupValue),
          data: {},
          aggregations: group.aggregatedValues
        });

        if (group.subRows && group.subRows.length > 0) {
          flatten(group.subRows, level + 1);
        } else if (group.originalRows) {
          group.originalRows.forEach(row => {
            flatData.push({
              isGroup: false,
              level: level + 1,
              data: row
            });
          });
        }
      });
    }

    flatten(groupedData);

    await exportGroupedToExcel(flatData, excelColumns, { filename });
    onExport?.(data);
  }, [groupedData, excelColumns, filename, onExport, data, handleExportToExcel]);

  return {
    exportToExcel: handleExportToExcel,
    exportGroupedToExcel: handleExportGroupedToExcel
  };
}
